import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  Trash2, 
  CreditCard, 
  Wallet,
  Home,
  Store,
  Bike,
  MapPin,
  ArrowLeft,
  CheckCircle,
  Search,
  X,
  Eye,
  EyeOff,
  Receipt,
  User,
  Phone,
  MessageSquare,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  TrendingUp,
  Scale,
  Edit3
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from './utils/apiConfig';

const PDVScreen = ({ onClose }) => {
  // Estados principais
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showProductImages, setShowProductImages] = useState(false);
  const [addingToCart, setAddingToCart] = useState(null);
  
  // Estados de checkout
  const [step, setStep] = useState('menu'); // menu, checkout
  const [deliveryType, setDeliveryType] = useState('balcao');
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [isFinalizingOrder, setIsFinalizingOrder] = useState(false);
  const [showMiniCart, setShowMiniCart] = useState(true);
  const [paidAmount, setPaidAmount] = useState('');
  const [address, setAddress] = useState({
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipcode: ''
  });
  
  // Modal states
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  
  // Delivery zones (bairros e taxas)
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [selectedDeliveryZone, setSelectedDeliveryZone] = useState(null);
  const [loadingZones, setLoadingZones] = useState(false);
  
  // ========== ESTADOS DO MODAL DE ADICIONAIS ==========
  const [additionalGroups, setAdditionalGroups] = useState([]);
  const [showAdditionalsModal, setShowAdditionalsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedAdditionals, setSelectedAdditionals] = useState({});
  const [productQuantity, setProductQuantity] = useState(1);
  const [loadingAdditionals, setLoadingAdditionals] = useState(false);
  const [productGroupsWithItems, setProductGroupsWithItems] = useState([]); // Grupos com items carregados

  // ========== ESTADOS DO MODAL DE VENDA POR PESO ==========
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [weightItems, setWeightItems] = useState([]);
  const [selectedWeightItem, setSelectedWeightItem] = useState(null);
  const [weightInGrams, setWeightInGrams] = useState('');
  const [loadingWeightItems, setLoadingWeightItems] = useState(false);
  const [showNewWeightItemForm, setShowNewWeightItemForm] = useState(false);
  const [newWeightItemName, setNewWeightItemName] = useState('');
  const [newWeightItemPrice, setNewWeightItemPrice] = useState('');
  const [editingWeightItem, setEditingWeightItem] = useState(null);

  // Buscar bairros cadastrados
  const fetchDeliveryZones = async () => {
    setLoadingZones(true);
    try {
      const response = await axios.get(`${API}/delivery-zones`);
      setDeliveryZones(response.data || []);
    } catch (error) {
      console.error('Error fetching delivery zones:', error);
      setDeliveryZones([]);
    }
    setLoadingZones(false);
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchDeliveryZones();
    fetchAdditionalGroups();
    fetchWeightItems();
    
    // Bloqueia scroll do body quando PDV abre
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    // Restaura scroll quando PDV fecha
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.filter(p => p.available));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Erro ao carregar produtos');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.filter(c => c.active));
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Buscar grupos de adicionais
  const fetchAdditionalGroups = async () => {
    try {
      const response = await axios.get(`${API}/additional-groups`);
      setAdditionalGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching additional groups:', error);
    }
  };

  // ========== FUNÇÕES DE VENDA POR PESO ==========
  const fetchWeightItems = async () => {
    setLoadingWeightItems(true);
    try {
      const response = await axios.get(`${API}/weight-items`);
      setWeightItems(response.data || []);
    } catch (error) {
      console.error('Error fetching weight items:', error);
      setWeightItems([]);
    }
    setLoadingWeightItems(false);
  };

  const createWeightItem = async () => {
    if (!newWeightItemName.trim() || !newWeightItemPrice || parseFloat(newWeightItemPrice) <= 0) {
      toast.error('Preencha nome e preço válido');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/weight-items`, {
        name: newWeightItemName.trim(),
        price_per_kg: parseFloat(newWeightItemPrice)
      });
      
      setWeightItems([...weightItems, response.data]);
      setNewWeightItemName('');
      setNewWeightItemPrice('');
      setShowNewWeightItemForm(false);
      toast.success('Item criado com sucesso!');
    } catch (error) {
      console.error('Error creating weight item:', error);
      toast.error('Erro ao criar item');
    }
  };

  const updateWeightItem = async () => {
    if (!editingWeightItem) return;
    
    if (!newWeightItemName.trim() || !newWeightItemPrice || parseFloat(newWeightItemPrice) <= 0) {
      toast.error('Preencha nome e preço válido');
      return;
    }
    
    try {
      const response = await axios.put(`${API}/weight-items/${editingWeightItem.id}`, {
        name: newWeightItemName.trim(),
        price_per_kg: parseFloat(newWeightItemPrice)
      });
      
      setWeightItems(weightItems.map(item => 
        item.id === editingWeightItem.id ? response.data : item
      ));
      setEditingWeightItem(null);
      setNewWeightItemName('');
      setNewWeightItemPrice('');
      setShowNewWeightItemForm(false);
      toast.success('Item atualizado!');
    } catch (error) {
      console.error('Error updating weight item:', error);
      toast.error('Erro ao atualizar item');
    }
  };

  const deleteWeightItem = async (itemId) => {
    if (!window.confirm('Remover este item?')) return;
    
    try {
      await axios.delete(`${API}/weight-items/${itemId}`);
      setWeightItems(weightItems.filter(item => item.id !== itemId));
      
      if (selectedWeightItem?.id === itemId) {
        setSelectedWeightItem(null);
        setWeightInGrams('');
      }
      toast.success('Item removido!');
    } catch (error) {
      console.error('Error deleting weight item:', error);
      toast.error('Erro ao remover item');
    }
  };

  // Calcular total do item por peso
  const calculateWeightTotal = useMemo(() => {
    if (!selectedWeightItem || !weightInGrams || parseFloat(weightInGrams) <= 0) return 0;
    const grams = parseFloat(weightInGrams);
    return (grams / 1000) * selectedWeightItem.price_per_kg;
  }, [selectedWeightItem, weightInGrams]);

  // Adicionar item por peso ao carrinho
  const addWeightItemToCart = () => {
    if (!selectedWeightItem || !weightInGrams || calculateWeightTotal <= 0) return;
    
    const grams = parseFloat(weightInGrams);
    const weightLabel = grams >= 1000 ? `${(grams / 1000).toFixed(2).replace('.', ',')}kg` : `${grams}g`;
    
    const cartItem = {
      id: `weight-${selectedWeightItem.id}-${Date.now()}`,
      name: `${selectedWeightItem.name} (${weightLabel})`,
      price: calculateWeightTotal,
      quantity: 1,
      isWeightItem: true,
      weightItemId: selectedWeightItem.id,
      weightInGrams: grams,
      additionals: []
    };
    
    setCart([...cart, cartItem]);
    toast.success('Adicionado ao carrinho! 🎉', {
      description: `${cartItem.name} - R$ ${calculateWeightTotal.toFixed(2)}`,
      duration: 2000
    });
    
    // Reset modal state
    setSelectedWeightItem(null);
    setWeightInGrams('');
    setShowWeightModal(false);
  };

  // Abrir modal de peso
  const openWeightModal = () => {
    setSelectedWeightItem(null);
    setWeightInGrams('');
    setShowNewWeightItemForm(false);
    setEditingWeightItem(null);
    setNewWeightItemName('');
    setNewWeightItemPrice('');
    setShowWeightModal(true);
  };

  // Iniciar edição de item
  const startEditWeightItem = (item) => {
    setEditingWeightItem(item);
    setNewWeightItemName(item.name);
    setNewWeightItemPrice(item.price_per_kg.toString());
    setShowNewWeightItemForm(true);
  };

  // Buscar CEP com debounce
  const searchCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();
      
      if (!data.erro) {
        setAddress(prev => ({
          ...prev,
          street: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || '',
          zipcode: cep
        }));
        toast.success('CEP encontrado!');
      } else {
        toast.error('CEP não encontrado');
      }
    } catch (error) {
      toast.error('Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  };

  // Funções do carrinho com animação
  // ========== FUNÇÃO PARA OBTER GRUPOS DE ADICIONAIS DO PRODUTO ==========
  const getProductAdditionalGroups = useCallback((product) => {
    // Verificar se o produto tem additional_groups configurados
    const productGroupIds = product.additional_groups || [];
    if (productGroupIds.length === 0) return [];
    
    // Filtrar os grupos de adicionais que pertencem a este produto
    return additionalGroups.filter(group => 
      productGroupIds.includes(group.id) && group.active !== false
    );
  }, [additionalGroups]);

  // ========== ADICIONAR AO CARRINHO DIRETO (SEM ADICIONAIS) ==========
  const addToCartDirect = useCallback((product) => {
    setAddingToCart(product.id);
    
    const existingItem = cart.find(item => 
      item.id === product.id && (!item.additionals || item.additionals.length === 0)
    );
    
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id && (!item.additionals || item.additionals.length === 0)
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1, additionals: [] }]);
    }
    
    toast.success('Adicionado ao carrinho! 🎉', {
      description: `${product.name} - R$ ${product.price.toFixed(2)}`,
      duration: 2000
    });

    setTimeout(() => setAddingToCart(null), 500);
  }, [cart]);

  // ========== CARREGAR ADICIONAIS DE CADA GRUPO ==========
  const loadGroupAdditionals = useCallback(async (groupIds) => {
    setLoadingAdditionals(true);
    try {
      // Carregar adicionais de todos os grupos em paralelo
      const groupsPromises = groupIds.map(async (groupId) => {
        try {
          const response = await axios.get(`${API}/additional-groups/${groupId}/additionals`);
          const group = additionalGroups.find(g => g.id === groupId);
          return {
            ...group,
            id: groupId,
            name: response.data.group_name || group?.name || 'Grupo',
            additionals: response.data.additionals || [],
            min_items: group?.min_choices || 0,
            max_items: group?.max_choices || 10,
            is_required: group?.is_required || false
          };
        } catch (error) {
          console.error(`Error loading additionals for group ${groupId}:`, error);
          return null;
        }
      });
      
      const loadedGroups = await Promise.all(groupsPromises);
      const validGroups = loadedGroups.filter(g => g !== null && g.additionals && g.additionals.length > 0);
      setProductGroupsWithItems(validGroups);
    } catch (error) {
      console.error('Error loading group additionals:', error);
      setProductGroupsWithItems([]);
    } finally {
      setLoadingAdditionals(false);
    }
  }, [additionalGroups]);

  // ========== ABRIR MODAL DE ADICIONAIS OU ADICIONAR DIRETO ==========
  const handleProductClick = useCallback(async (product) => {
    const productGroupIds = product.additional_groups || [];
    
    if (productGroupIds.length > 0) {
      // Tem adicionais - abrir modal e carregar itens
      setSelectedProduct(product);
      setSelectedAdditionals({});
      setProductQuantity(1);
      setProductGroupsWithItems([]);
      setShowAdditionalsModal(true);
      
      // Carregar adicionais dos grupos
      await loadGroupAdditionals(productGroupIds);
    } else {
      // Não tem adicionais - adicionar direto
      addToCartDirect(product);
    }
  }, [loadGroupAdditionals, addToCartDirect]);

  // ========== TOGGLE DE ADICIONAL NO MODAL ==========
  const toggleAdditional = useCallback((groupId, additional) => {
    setSelectedAdditionals(prev => {
      const groupAdditionals = prev[groupId] || [];
      const group = productGroupsWithItems.find(g => g.id === groupId);
      const maxItems = group?.max_items || 10;
      
      const isSelected = groupAdditionals.some(a => a.id === additional.id);
      
      if (isSelected) {
        // Remove o adicional
        return {
          ...prev,
          [groupId]: groupAdditionals.filter(a => a.id !== additional.id)
        };
      } else {
        // Adiciona o adicional (respeitando o máximo)
        if (groupAdditionals.length >= maxItems) {
          toast.error(`Máximo de ${maxItems} itens neste grupo`);
          return prev;
        }
        return {
          ...prev,
          [groupId]: [...groupAdditionals, additional]
        };
      }
    });
  }, [productGroupsWithItems]);

  // ========== VERIFICAR SE PODE ADICIONAR (GRUPOS OBRIGATÓRIOS) ==========
  const canAddToCart = useMemo(() => {
    if (!selectedProduct) return false;
    if (loadingAdditionals) return false;
    
    // Se não tem grupos com itens, pode adicionar
    if (productGroupsWithItems.length === 0) return true;
    
    for (const group of productGroupsWithItems) {
      const minItems = group.min_items || 0;
      const selectedCount = (selectedAdditionals[group.id] || []).length;
      
      if (minItems > 0 && selectedCount < minItems) {
        return false;
      }
    }
    return true;
  }, [selectedProduct, selectedAdditionals, productGroupsWithItems, loadingAdditionals]);

  // ========== CALCULAR TOTAL DO PRODUTO COM ADICIONAIS ==========
  const calculateProductTotal = useMemo(() => {
    if (!selectedProduct) return 0;
    
    let total = selectedProduct.price * productQuantity;
    
    Object.values(selectedAdditionals).forEach(additionals => {
      additionals.forEach(add => {
        total += (add.price || 0) * productQuantity;
      });
    });
    
    return total;
  }, [selectedProduct, selectedAdditionals, productQuantity]);

  // ========== ADICIONAR AO CARRINHO COM ADICIONAIS ==========
  const addToCartWithAdditionals = useCallback(() => {
    if (!selectedProduct || !canAddToCart) return;
    
    // Montar lista de adicionais selecionados
    const allAdditionals = [];
    Object.entries(selectedAdditionals).forEach(([groupId, additionals]) => {
      const group = productGroupsWithItems.find(g => g.id === groupId);
      additionals.forEach(add => {
        allAdditionals.push({
          ...add,
          group_name: group?.name || '',
          group_id: groupId
        });
      });
    });
    
    // Calcular preço com adicionais
    const additionalsTotal = allAdditionals.reduce((sum, a) => sum + (a.price || 0), 0);
    const totalPrice = selectedProduct.price + additionalsTotal;
    
    // Criar item único do carrinho (com ID único para permitir mesmo produto com adicionais diferentes)
    const cartItemId = `${selectedProduct.id}_${Date.now()}`;
    
    const cartItem = {
      ...selectedProduct,
      cartItemId,
      quantity: productQuantity,
      additionals: allAdditionals,
      totalPrice
    };
    
    setCart(prev => [...prev, cartItem]);
    
    // Feedback
    const additionalsText = allAdditionals.length > 0 
      ? ` + ${allAdditionals.length} adicional(is)` 
      : '';
    toast.success('Adicionado ao carrinho! 🎉', {
      description: `${selectedProduct.name}${additionalsText}`,
      duration: 2000
    });
    
    // Fechar modal e resetar estados
    setShowAdditionalsModal(false);
    setSelectedProduct(null);
    setSelectedAdditionals({});
    setProductQuantity(1);
    setProductGroupsWithItems([]);
  }, [selectedProduct, selectedAdditionals, productQuantity, canAddToCart, productGroupsWithItems]);

  // ========== FUNÇÃO LEGADA (para compatibilidade) ==========
  const addToCart = useCallback((product) => {
    handleProductClick(product);
  }, [handleProductClick]);

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(cart.map(item => 
      (item.cartItemId || item.id) === productId 
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => (item.cartItemId || item.id) !== productId));
    toast.info('Produto removido', { duration: 1500 });
  };

  const clearCart = () => {
    setCart([]);
    toast.info('Carrinho limpo');
  };

  // Cálculos memoizados
  const { subtotal, deliveryFee, total, itemCount } = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => {
      const itemPrice = item.totalPrice || item.price;
      return sum + (itemPrice * item.quantity);
    }, 0);
    const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    const deliveryFee = (deliveryType === 'delivery' && selectedDeliveryZone) 
      ? selectedDeliveryZone.fee 
      : 0;
    return { subtotal, deliveryFee, total: subtotal + deliveryFee, itemCount };
  }, [cart, deliveryType, selectedDeliveryZone]);

  // Calcular troco
  const changeAmount = useMemo(() => {
    if (paymentMethod === 'dinheiro' && paidAmount) {
      const paid = parseFloat(paidAmount);
      if (!isNaN(paid) && paid >= total) {
        return paid - total;
      }
    }
    return 0;
  }, [paymentMethod, paidAmount, total]);

  // Filtrar produtos com debounce
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesCategory = selectedCategory === 'all' || product.category_id === selectedCategory;
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            product.description?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [products, selectedCategory, searchTerm]);

  // Categorias com contagem
  const categoriesWithCount = useMemo(() => {
    return categories.map(cat => ({
      ...cat,
      count: products.filter(p => p.category_id === cat.id).length
    }));
  }, [categories, products]);

  // Finalizar pedido
  const finalizarPedido = async () => {
    if (cart.length === 0) {
      toast.error('Adicione produtos ao carrinho');
      return;
    }

    if (!customerName.trim()) {
      toast.error('Informe o nome do cliente');
      return;
    }

    if (deliveryType === 'delivery' && (!address.street || !address.number || !selectedDeliveryZone)) {
      toast.error('Preencha o endereço de entrega completo');
      setShowAddressModal(true);
      return;
    }

    if (deliveryType === 'mesa' && !tableNumber) {
      toast.error('Informe o número da mesa');
      return;
    }

    setIsFinalizingOrder(true);

    try {
      const orderNumber = `${Date.now().toString().slice(-6)}`;
      
      const orderData = {
        order_number: orderNumber,
        customer_name: customerName,
        customer_phone: customerPhone,
        table_number: deliveryType === 'mesa' ? tableNumber : null,
        delivery_type: deliveryType,
        payment_method: paymentMethod,
        address: deliveryType === 'delivery' ? {
          ...address,
          neighborhood: selectedDeliveryZone?.name || address.neighborhood
        } : null,
        delivery_fee: deliveryFee,
        notes: orderNotes || null,
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.price,
          additionals: (item.additionals || []).map(add => ({
            id: add.id,
            name: add.name,
            additional_name: add.name,
            price: add.price || 0,
            group_name: add.group_name || '',
            group_id: add.group_id || ''
          }))
        })),
        total: total,
        status: 'pendente'
      };

      await axios.post(`${API}/orders`, orderData);
      
      toast.success('Pedido criado com sucesso! 🎉', {
        description: `Pedido #${orderNumber} - R$ ${total.toFixed(2)}`,
        duration: 4000
      });

      // Limpar formulário
      clearCart();
      setCustomerName('');
      setCustomerPhone('');
      setTableNumber('');
      setOrderNotes('');
      setPaidAmount('');
      setAddress({
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        zipcode: ''
      });
      setSelectedDeliveryZone(null);
      setStep('menu');
      
      setTimeout(() => onClose(), 1500);

    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Erro ao criar pedido', {
        description: error.response?.data?.detail || 'Tente novamente'
      });
    } finally {
      setIsFinalizingOrder(false);
    }
  };

  // Loading state
  if (loading) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-[999999] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg font-medium">Carregando PDV...</p>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div 
      id="pdv-container" 
      className="fixed inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-50 z-[999999] flex flex-col lg:flex-row"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, height: '100vh', overflow: 'hidden', zIndex: 999999 }}
    >
      {/* ========== HEADER MOBILE/TABLET ========== */}
      <div className="lg:hidden bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 shadow-2xl flex-shrink-0 z-[100000] sticky top-0">
        <div className="px-3 py-2.5 safe-area-top">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={step === 'checkout' ? () => setStep('menu') : onClose}
                className="w-11 h-11 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-2xl flex items-center justify-center transition-all active:scale-95"
                data-testid="close-pdv-button"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div>
                <h1 className="text-white font-bold text-xl tracking-tight">{step === 'checkout' ? 'Finalizar' : 'PDV'}</h1>
                <p className="text-orange-100 text-xs font-medium">{step === 'checkout' ? 'Pedido' : 'Novo Pedido'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {step === 'menu' && (
                <>
                  <button
                    onClick={openWeightModal}
                    className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl p-2.5 transition-all active:scale-95"
                    title="Venda por Peso"
                    data-testid="weight-button-mobile"
                  >
                    <Scale className="w-5 h-5 text-white" />
                  </button>
                  <button
                    onClick={() => setShowProductImages(!showProductImages)}
                    className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl p-2.5 transition-all active:scale-95"
                    title={showProductImages ? "Ocultar imagens" : "Mostrar imagens"}
                    data-testid="toggle-images-button"
                  >
                    {showProductImages ? (
                      <Eye className="w-5 h-5 text-white" />
                    ) : (
                      <EyeOff className="w-5 h-5 text-white" />
                    )}
                  </button>
                </>
              )}
              
              <button
                onClick={() => setStep(step === 'menu' ? 'checkout' : 'menu')}
                className="relative bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl p-2.5 transition-all active:scale-95"
                data-testid="cart-button"
              >
                <ShoppingCart className="w-6 h-6 text-white" />
                {cart.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center animate-pulse px-1.5">
                    {itemCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Mini Cart Summary - Removed to save space */}
        </div>
      </div>

      {/* ========== MAIN CONTENT AREA ========== */}
      <div className="flex-1 flex flex-col lg:flex-row h-full">
        
        {/* ========== LEFT SIDE - PRODUCTS (Mobile: full, Desktop: 65%) ========== */}
        <div className={`flex-1 lg:w-[65%] flex flex-col h-full ${step === 'checkout' ? 'hidden lg:flex' : 'flex'}`}>
          
          {/* Desktop Header */}
          <div className="hidden lg:block bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 shadow-xl px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={onClose}
                  className="w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-2xl flex items-center justify-center transition-all"
                  data-testid="close-pdv-button-desktop"
                >
                  <ArrowLeft className="w-6 h-6 text-white" />
                </button>
                <div>
                  <h1 className="text-white font-bold text-2xl tracking-tight">Ponto de Venda</h1>
                  <p className="text-orange-100 text-sm font-medium">Selecione os produtos e finalize o pedido</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={openWeightModal}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl px-4 py-2.5 transition-all flex items-center gap-2"
                  data-testid="weight-button-desktop"
                >
                  <Scale className="w-5 h-5 text-white" />
                  <span className="text-white text-sm font-medium">Peso</span>
                </button>
                <button
                  onClick={() => setShowProductImages(!showProductImages)}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl px-4 py-2.5 transition-all flex items-center gap-2"
                  data-testid="toggle-images-button-desktop"
                >
                  {showProductImages ? (
                    <>
                      <Eye className="w-5 h-5 text-white" />
                      <span className="text-white text-sm font-medium">Ocultar Imagens</span>
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-5 h-5 text-white" />
                      <span className="text-white text-sm font-medium">Mostrar Imagens</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {step === 'menu' ? (
            <div className="flex-1 flex flex-col h-full bg-gradient-to-br from-slate-50 to-white">
              {/* Search Bar */}
              <div className="p-4 lg:p-6 bg-white border-b border-slate-200 flex-shrink-0 z-10">
                <div className="relative max-w-2xl">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar produtos por nome ou descrição..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-12 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-sm lg:text-base"
                    data-testid="search-input"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Categories */}
              <div className="px-4 lg:px-6 py-3 bg-white border-b border-slate-200 flex-shrink-0">
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-5 py-2.5 rounded-xl whitespace-nowrap text-sm font-bold transition-all flex-shrink-0 ${
                      selectedCategory === 'all'
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/30'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid="category-all"
                  >
                    Todos ({products.length})
                  </button>
                  {categoriesWithCount.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-5 py-2.5 rounded-xl whitespace-nowrap text-sm font-bold transition-all flex-shrink-0 ${
                        selectedCategory === category.id
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/30'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`category-${category.id}`}
                    >
                      {category.name} ({category.count})
                    </button>
                  ))}
                </div>
              </div>

              {/* Products Grid */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 lg:p-6 pdv-scroll" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', maxHeight: '100%' }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 max-w-7xl pb-8">
                  {filteredProducts.map((product) => {
                    const isAdding = addingToCart === product.id;
                    const inCart = cart.find(item => item.id === product.id);
                    
                    return (
                      <div
                        key={product.id}
                        className={`bg-white rounded-2xl p-4 border-2 border-slate-200 hover:border-orange-500 hover:shadow-xl transition-all duration-300 group ${isAdding ? 'scale-95 border-orange-500' : ''}`}
                        data-testid={`product-${product.id}`}
                      >
                        {/* Image */}
                        {showProductImages && (
                          <div className="relative aspect-square bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl mb-3 overflow-hidden group-hover:shadow-lg transition-shadow">
                            {product.image_url ? (
                              <img 
                                src={product.image_url} 
                                alt={product.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextElementSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className="absolute inset-0 flex items-center justify-center text-6xl opacity-30" style={{ display: product.image_url ? 'none' : 'flex' }}>
                              🍽️
                            </div>
                            
                            {/* Badge if in cart */}
                            {inCart && (
                              <div className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-bold rounded-full px-2.5 py-1 shadow-lg flex items-center gap-1">
                                <ShoppingCart className="w-3 h-3" />
                                {inCart.quantity}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Product Info */}
                        <div className="flex-1">
                          <h3 className="text-slate-800 font-bold text-base lg:text-lg mb-1 line-clamp-2 group-hover:text-orange-600 transition-colors">
                            {product.name}
                          </h3>
                          {product.description && (
                            <p className="text-slate-500 text-xs lg:text-sm line-clamp-2 mb-3">
                              {product.description}
                            </p>
                          )}
                        </div>

                        {/* Price and Add Button */}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                          <span className="text-orange-600 font-bold text-xl lg:text-2xl">
                            R$ {product.price.toFixed(2)}
                          </span>
                          <button
                            onClick={() => addToCart(product)}
                            disabled={isAdding}
                            className={`bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl px-4 py-2.5 font-bold text-sm transition-all flex items-center gap-2 shadow-lg hover:shadow-xl active:scale-95 ${isAdding ? 'opacity-50' : ''}`}
                          >
                            <Plus className="w-4 h-4" />
                            Adicionar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {filteredProducts.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <Search className="w-20 h-20 mb-4 opacity-20" />
                    <p className="text-xl font-medium text-slate-500">Nenhum produto encontrado</p>
                    <p className="text-sm text-slate-400 mt-2">Tente buscar com outros termos</p>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* ========== RIGHT SIDE - CART/CHECKOUT (Mobile: full screen, Desktop: 35% sidebar) ========== */}
        <div className={`flex-1 lg:w-[35%] lg:flex-shrink-0 flex flex-col bg-white lg:border-l-2 lg:border-slate-200 h-full ${step === 'menu' ? 'hidden lg:flex' : 'flex'}`}>

          {/* Desktop Checkout Header */}
          <div className="hidden lg:block bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-bold text-xl flex items-center gap-2">
                <ShoppingCart className="w-6 h-6" />
                Carrinho
              </h2>
              {cart.length > 0 && (
                <span className="bg-orange-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                  {itemCount} {itemCount === 1 ? 'item' : 'itens'}
                </span>
              )}
            </div>
          </div>

          {/* Cart Content - Scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 lg:px-6 lg:py-4 space-y-3 pdv-scroll" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', paddingBottom: '140px' }}>
            
            {/* Cart Items */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-3 border-2 border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-slate-800 font-bold text-base lg:text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-orange-500" />
                  Itens ({cart.length})
                </h3>
                {cart.length > 0 && (
                  <button
                    onClick={clearCart}
                    className="text-red-500 hover:text-red-600 text-sm font-medium flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    Limpar
                  </button>
                )}
              </div>

              {cart.length > 0 ? (
                <div className="space-y-2">
                  {cart.map((item) => (
                    <div key={item.cartItemId || item.id} className="flex items-start gap-3 bg-white rounded-xl p-3 border border-slate-200 hover:border-orange-500 transition-all">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-800 font-semibold text-sm truncate">{item.name}</p>
                        {/* Mostrar adicionais se houver */}
                        {item.additionals && item.additionals.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {item.additionals.map((add, idx) => (
                              <p key={idx} className="text-slate-500 text-xs flex items-center gap-1">
                                <span className="text-orange-400">+</span>
                                {add.name}
                                {add.price > 0 && <span className="text-slate-400">R$ {add.price.toFixed(2)}</span>}
                              </p>
                            ))}
                          </div>
                        )}
                        <p className="text-orange-600 font-bold text-base mt-0.5">
                          R$ {(item.totalPrice || item.price).toFixed(2)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity - 1)}
                          className="w-8 h-8 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center justify-center transition-all active:scale-90"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="text-slate-800 font-bold w-10 text-center text-base">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity + 1)}
                          className="w-8 h-8 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg flex items-center justify-center transition-all active:scale-90"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.cartItemId || item.id)}
                          className="ml-1 text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ShoppingCart className="w-16 h-16 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-400 font-medium">Carrinho vazio</p>
                  <p className="text-slate-400 text-sm mt-1">Adicione produtos para continuar</p>
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <>
                {/* Customer Data */}
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-3 border-2 border-blue-200">
                  <h3 className="text-slate-800 font-bold text-base lg:text-lg mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Dados do Cliente
                  </h3>
                  <div className="space-y-3">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Nome do cliente *"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        data-testid="customer-name-input"
                      />
                    </div>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="tel"
                        placeholder="Telefone (opcional)"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        data-testid="customer-phone-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Delivery Type */}
                <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-3 border-2 border-purple-200">
                  <h3 className="text-slate-800 font-bold text-base lg:text-lg mb-3 flex items-center gap-2">
                    <Bike className="w-5 h-5 text-purple-600" />
                    Tipo de Entrega
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setDeliveryType('delivery')}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        deliveryType === 'delivery'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="delivery-type-delivery"
                    >
                      <Bike className={`w-5 h-5 mx-auto mb-1 ${deliveryType === 'delivery' ? 'text-orange-600' : 'text-slate-400'}`} />
                      <p className={`text-xs lg:text-sm font-bold ${deliveryType === 'delivery' ? 'text-orange-600' : 'text-slate-600'}`}>Delivery</p>
                    </button>
                    <button
                      onClick={() => setDeliveryType('balcao')}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        deliveryType === 'balcao'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="delivery-type-balcao"
                    >
                      <Store className={`w-5 h-5 mx-auto mb-1 ${deliveryType === 'balcao' ? 'text-orange-600' : 'text-slate-400'}`} />
                      <p className={`text-xs lg:text-sm font-bold ${deliveryType === 'balcao' ? 'text-orange-600' : 'text-slate-600'}`}>Balcão</p>
                    </button>
                    <button
                      onClick={() => setDeliveryType('mesa')}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        deliveryType === 'mesa'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="delivery-type-mesa"
                    >
                      <Home className={`w-5 h-5 mx-auto mb-1 ${deliveryType === 'mesa' ? 'text-orange-600' : 'text-slate-400'}`} />
                      <p className={`text-xs lg:text-sm font-bold ${deliveryType === 'mesa' ? 'text-orange-600' : 'text-slate-600'}`}>Mesa</p>
                    </button>
                  </div>

                  {deliveryType === 'delivery' && (
                    <button
                      onClick={() => setShowAddressModal(true)}
                      className={`w-full mt-3 px-4 py-3 bg-white hover:bg-slate-50 border-2 rounded-xl text-slate-800 flex items-center justify-between transition-all ${
                        address.street && selectedDeliveryZone ? 'border-green-400' : 'border-slate-200'
                      }`}
                      data-testid="open-address-modal"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <MapPin className={`w-5 h-5 flex-shrink-0 ${address.street && selectedDeliveryZone ? 'text-green-600' : 'text-orange-600'}`} />
                        <div className="flex flex-col items-start min-w-0">
                          {address.street && selectedDeliveryZone ? (
                            <>
                              <span className="text-sm font-medium truncate max-w-full">
                                {address.street}, {address.number}
                              </span>
                              <span className="text-xs text-green-600 font-semibold">
                                {selectedDeliveryZone.name} • Taxa: R$ {selectedDeliveryZone.fee.toFixed(2)}
                              </span>
                            </>
                          ) : (
                            <span className="text-sm font-medium text-orange-600">
                              📍 Adicionar endereço
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`font-bold flex-shrink-0 ${address.street && selectedDeliveryZone ? 'text-green-600' : 'text-orange-600'}`}>
                        {address.street && selectedDeliveryZone ? '✓' : '→'}
                      </span>
                    </button>
                  )}

                  {deliveryType === 'mesa' && (
                    <input
                      type="text"
                      placeholder="Número da mesa *"
                      value={tableNumber}
                      onChange={(e) => setTableNumber(e.target.value)}
                      className="w-full mt-3 px-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      data-testid="table-number-input"
                    />
                  )}
                </div>

                {/* Payment Method */}
                <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-3 border-2 border-green-200">
                  <h3 className="text-slate-800 font-bold text-base lg:text-lg mb-3 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-green-600" />
                    Forma de Pagamento
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setPaymentMethod('dinheiro')}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        paymentMethod === 'dinheiro'
                          ? 'border-green-500 bg-green-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="payment-method-dinheiro"
                    >
                      <Wallet className={`w-5 h-5 mx-auto mb-1 ${paymentMethod === 'dinheiro' ? 'text-green-600' : 'text-slate-400'}`} />
                      <p className={`text-xs lg:text-sm font-bold ${paymentMethod === 'dinheiro' ? 'text-green-600' : 'text-slate-600'}`}>Dinheiro</p>
                    </button>
                    <button
                      onClick={() => {
                        setPaymentMethod('cartao');
                        setPaidAmount('');
                      }}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        paymentMethod === 'cartao'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="payment-method-cartao"
                    >
                      <CreditCard className={`w-5 h-5 mx-auto mb-1 ${paymentMethod === 'cartao' ? 'text-blue-600' : 'text-slate-400'}`} />
                      <p className={`text-xs lg:text-sm font-bold ${paymentMethod === 'cartao' ? 'text-blue-600' : 'text-slate-600'}`}>Cartão</p>
                    </button>
                    <button
                      onClick={() => {
                        setPaymentMethod('pix');
                        setPaidAmount('');
                      }}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        paymentMethod === 'pix'
                          ? 'border-cyan-500 bg-cyan-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="payment-method-pix"
                    >
                      <span className={`text-xl lg:text-2xl font-bold mx-auto mb-1 block ${paymentMethod === 'pix' ? 'text-cyan-600' : 'text-slate-400'}`}>PIX</span>
                      <p className={`text-xs lg:text-sm font-bold ${paymentMethod === 'pix' ? 'text-cyan-600' : 'text-slate-600'}`}>PIX</p>
                    </button>
                    <button
                      onClick={() => {
                        setPaymentMethod('credito');
                        setPaidAmount('');
                      }}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        paymentMethod === 'credito'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="payment-method-credito"
                    >
                      <CreditCard className={`w-5 h-5 mx-auto mb-1 ${paymentMethod === 'credito' ? 'text-purple-600' : 'text-slate-400'}`} />
                      <p className={`text-xs lg:text-sm font-bold ${paymentMethod === 'credito' ? 'text-purple-600' : 'text-slate-600'}`}>Crédito</p>
                    </button>
                  </div>

                  {/* Campo de Troco - Aparece quando Dinheiro é selecionado */}
                  {paymentMethod === 'dinheiro' && (
                    <div className="mt-3 space-y-2 animate-in slide-in-from-top">
                      <div className="relative">
                        <label className="text-slate-700 text-sm font-semibold mb-1 block">
                          Valor Pago pelo Cliente
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 font-bold">R$</span>
                          <input
                            type="number"
                            step="0.01"
                            min={total}
                            placeholder="0,00"
                            value={paidAmount}
                            onChange={(e) => setPaidAmount(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-white border-2 border-green-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base font-semibold"
                            data-testid="paid-amount-input"
                          />
                        </div>
                      </div>

                      {/* Mostrar Troco */}
                      {paidAmount && parseFloat(paidAmount) >= total && (
                        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-3 shadow-lg animate-in scale-in">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="bg-white/20 rounded-lg p-2">
                                <Wallet className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="text-white text-xs font-medium">Troco</p>
                                <p className="text-white font-bold text-2xl">R$ {changeAmount.toFixed(2)}</p>
                              </div>
                            </div>
                            <CheckCircle className="w-8 h-8 text-white" />
                          </div>
                        </div>
                      )}

                      {/* Aviso se valor for menor */}
                      {paidAmount && parseFloat(paidAmount) < total && (
                        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-2.5 flex items-center gap-2">
                          <span className="text-red-600 text-sm font-semibold">
                            ⚠️ Valor pago é menor que o total do pedido
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Order Notes */}
                <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-3 border-2 border-amber-200">
                  <h3 className="text-slate-800 font-bold text-base mb-2 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-amber-600" />
                    Observações (Opcional)
                  </h3>
                  <textarea
                    placeholder="Ex: Sem cebola, caprichar no molho..."
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm resize-none"
                    data-testid="order-notes-input"
                  />
                </div>
              </>
            )}
          </div>

          {/* Total and Finalize - Fixed Bottom */}
          {cart.length > 0 && (
            <div className="fixed bottom-0 left-0 right-0 lg:relative lg:bottom-auto flex-shrink-0 bg-gradient-to-br from-slate-800 to-slate-900 px-3 py-3 lg:px-6 lg:py-4 border-t-4 border-orange-500 shadow-2xl z-[100001] safe-area-bottom">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-slate-300 text-xs font-medium">Total do Pedido</p>
                  <p className="text-white font-bold text-2xl lg:text-3xl">R$ {total.toFixed(2)}</p>
                  <div className="flex flex-col text-xs">
                    <span className="text-slate-400">{itemCount} {itemCount === 1 ? 'item' : 'itens'} • Subtotal: R$ {subtotal.toFixed(2)}</span>
                    {deliveryType === 'delivery' && deliveryFee > 0 && (
                      <span className="text-orange-400">+ Taxa de entrega: R$ {deliveryFee.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={finalizarPedido}
                disabled={isFinalizingOrder}
                className="w-full py-3.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-2xl transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-base"
                data-testid="finalize-order-button"
              >
                {isFinalizingOrder ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Finalizando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Finalizar Pedido
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ========== ADDRESS MODAL - NOVO DESIGN ========== */}
      {showAddressModal && (
        <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4">
          {/* Overlay escuro */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowAddressModal(false)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-300">
            {/* Header com gradiente */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-white text-xl font-bold">Endereço de Entrega</h2>
                    <p className="text-orange-100 text-sm">Informe onde entregar o pedido</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddressModal(false)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-200px)]">
              
              {/* Seleção de Bairro - OBRIGATÓRIO */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
                  <Bike className="w-4 h-4 text-orange-500" />
                  Bairro de Entrega *
                </label>
                
                {loadingZones ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                    <span className="ml-2 text-slate-500">Carregando bairros...</span>
                  </div>
                ) : deliveryZones.length === 0 ? (
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 text-center">
                    <p className="text-yellow-700 font-medium">⚠️ Nenhum bairro cadastrado</p>
                    <p className="text-yellow-600 text-sm mt-1">Cadastre bairros em "Bairros e Taxas"</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto pr-1">
                    {deliveryZones.map((zone) => (
                      <button
                        key={zone.id}
                        type="button"
                        onClick={() => {
                          setSelectedDeliveryZone(zone);
                          setAddress(prev => ({ ...prev, neighborhood: zone.name }));
                        }}
                        className={`w-full p-3 rounded-xl border-2 transition-all text-left flex items-center justify-between ${
                          selectedDeliveryZone?.id === zone.id
                            ? 'border-orange-500 bg-orange-50 shadow-md'
                            : 'border-slate-200 bg-white hover:border-orange-300 hover:bg-orange-50/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            selectedDeliveryZone?.id === zone.id 
                              ? 'bg-orange-500 text-white' 
                              : 'bg-slate-100 text-slate-500'
                          }`}>
                            <MapPin className="w-5 h-5" />
                          </div>
                          <div>
                            <p className={`font-semibold ${
                              selectedDeliveryZone?.id === zone.id ? 'text-orange-700' : 'text-slate-700'
                            }`}>
                              {zone.name}
                            </p>
                            {zone.estimated_time && (
                              <p className="text-xs text-slate-400">⏱️ {zone.estimated_time}</p>
                            )}
                          </div>
                        </div>
                        <div className={`px-3 py-1.5 rounded-lg font-bold text-sm ${
                          selectedDeliveryZone?.id === zone.id
                            ? 'bg-orange-500 text-white'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          R$ {zone.fee.toFixed(2)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Divisor */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200"></div>
                <span className="text-slate-400 text-xs font-medium">ENDEREÇO COMPLETO</span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>

              {/* Rua */}
              <div className="space-y-1">
                <label className="text-slate-600 text-sm font-medium">Nome da Rua *</label>
                <input
                  type="text"
                  placeholder="Ex: Rua das Flores"
                  value={address.street}
                  onChange={(e) => setAddress({ ...address, street: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                />
              </div>

              {/* Número e Complemento */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-slate-600 text-sm font-medium">Número *</label>
                  <input
                    type="text"
                    placeholder="123"
                    value={address.number}
                    onChange={(e) => setAddress({ ...address, number: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-slate-600 text-sm font-medium">Complemento</label>
                  <input
                    type="text"
                    placeholder="Apto, Bloco..."
                    value={address.complement}
                    onChange={(e) => setAddress({ ...address, complement: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  />
                </div>
              </div>

              {/* Preview do Endereço */}
              {selectedDeliveryZone && address.street && address.number && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-green-700 font-semibold">Endereço completo:</p>
                      <p className="text-green-600 text-sm">
                        {address.street}, {address.number}
                        {address.complement && ` - ${address.complement}`}
                      </p>
                      <p className="text-green-600 text-sm font-medium">
                        {selectedDeliveryZone.name}
                      </p>
                      <p className="text-green-500 text-xs mt-1">
                        Taxa de entrega: R$ {selectedDeliveryZone.fee.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer com botões */}
            <div className="border-t border-slate-100 px-6 py-4 bg-slate-50 flex gap-3">
              <button
                onClick={() => setShowAddressModal(false)}
                className="flex-1 px-4 py-3.5 bg-white hover:bg-slate-100 border-2 border-slate-200 text-slate-700 rounded-xl transition-all font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (!selectedDeliveryZone) {
                    toast.error('Selecione um bairro de entrega');
                    return;
                  }
                  if (!address.street.trim()) {
                    toast.error('Informe o nome da rua');
                    return;
                  }
                  if (!address.number.trim()) {
                    toast.error('Informe o número');
                    return;
                  }
                  setShowAddressModal(false);
                  toast.success('Endereço salvo! 📍', {
                    description: `${address.street}, ${address.number} - ${selectedDeliveryZone.name}`
                  });
                }}
                disabled={!selectedDeliveryZone || !address.street || !address.number}
                className="flex-1 px-4 py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Confirmar Endereço
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== MODAL DE VENDA POR PESO ========== */}
      {showWeightModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000001] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header do Modal */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Scale className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-white font-bold text-xl">Venda por Peso</h2>
                  <p className="text-emerald-100 text-sm">Selecione o item e informe o peso</p>
                </div>
              </div>
              <button
                onClick={() => setShowWeightModal(false)}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
                data-testid="close-weight-modal"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Corpo do Modal */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loadingWeightItems ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-10 h-10 text-emerald-500 animate-spin mb-3" />
                  <p className="text-slate-500 font-medium">Carregando itens...</p>
                </div>
              ) : (
                <>
                  {/* Lista de Itens Cadastrados */}
                  <div className="bg-slate-50 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-slate-800">Itens Cadastrados</h3>
                      <button
                        onClick={() => {
                          setShowNewWeightItemForm(!showNewWeightItemForm);
                          setEditingWeightItem(null);
                          setNewWeightItemName('');
                          setNewWeightItemPrice('');
                        }}
                        className="text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Novo Item
                      </button>
                    </div>

                    {/* Formulário de Novo Item / Edição */}
                    {showNewWeightItemForm && (
                      <div className="bg-white rounded-xl p-3 mb-3 border-2 border-emerald-200">
                        <div className="flex flex-col sm:flex-row gap-2 mb-2">
                          <input
                            type="text"
                            placeholder="Nome do item"
                            value={newWeightItemName}
                            onChange={(e) => setNewWeightItemName(e.target.value)}
                            className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">R$</span>
                            <input
                              type="number"
                              placeholder="0,00"
                              value={newWeightItemPrice}
                              onChange={(e) => setNewWeightItemPrice(e.target.value)}
                              step="0.01"
                              min="0"
                              className="w-full sm:w-28 pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={editingWeightItem ? updateWeightItem : createWeightItem}
                            className="flex-1 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-all"
                          >
                            {editingWeightItem ? 'Salvar' : 'Criar Item'}
                          </button>
                          <button
                            onClick={() => {
                              setShowNewWeightItemForm(false);
                              setEditingWeightItem(null);
                              setNewWeightItemName('');
                              setNewWeightItemPrice('');
                            }}
                            className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded-lg text-sm font-medium transition-all"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Lista de Itens */}
                    {weightItems.length === 0 ? (
                      <div className="text-center py-6 text-slate-400">
                        <Scale className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Nenhum item cadastrado</p>
                        <p className="text-xs">Clique em "Novo Item" para adicionar</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {weightItems.map(item => (
                          <div
                            key={item.id}
                            onClick={() => {
                              setSelectedWeightItem(item);
                              setWeightInGrams('');
                            }}
                            className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${
                              selectedWeightItem?.id === item.id
                                ? 'bg-emerald-100 border-2 border-emerald-500 shadow-md'
                                : 'bg-white border-2 border-slate-200 hover:border-emerald-300'
                            }`}
                          >
                            <div className="flex-1">
                              <p className={`font-medium ${selectedWeightItem?.id === item.id ? 'text-emerald-700' : 'text-slate-700'}`}>
                                {item.name}
                              </p>
                              <p className={`text-sm ${selectedWeightItem?.id === item.id ? 'text-emerald-600' : 'text-slate-500'}`}>
                                R$ {item.price_per_kg.toFixed(2)}/kg
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditWeightItem(item);
                                }}
                                className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteWeightItem(item.id);
                                }}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Seção de Informar Peso (aparece ao selecionar item) */}
                  {selectedWeightItem && (
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-4 border-2 border-emerald-200">
                      <h3 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
                        <Scale className="w-5 h-5" />
                        INFORMAR PESO
                      </h3>
                      
                      {/* Item Selecionado */}
                      <div className="bg-white rounded-xl p-3 mb-4">
                        <p className="font-semibold text-slate-800">{selectedWeightItem.name}</p>
                        <p className="text-emerald-600 text-sm font-medium">
                          R$ {selectedWeightItem.price_per_kg.toFixed(2)} por kg
                        </p>
                      </div>

                      {/* Input de Peso */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-600 mb-2">Peso em gramas:</label>
                        <div className="relative">
                          <input
                            type="number"
                            placeholder="Ex: 500"
                            value={weightInGrams}
                            onChange={(e) => setWeightInGrams(e.target.value)}
                            min="1"
                            step="1"
                            className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-lg font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">g</span>
                        </div>
                      </div>

                      {/* Presets de Peso */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-600 mb-2">Pesos rápidos:</label>
                        <div className="flex flex-wrap gap-2">
                          {[100, 250, 500, 750, 1000].map(grams => (
                            <button
                              key={grams}
                              onClick={() => setWeightInGrams(grams.toString())}
                              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                weightInGrams === grams.toString()
                                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                                  : 'bg-white text-slate-600 border-2 border-slate-200 hover:border-emerald-300'
                              }`}
                            >
                              {grams >= 1000 ? `${grams / 1000}kg` : `${grams}g`}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Card de Total */}
                      {weightInGrams && parseFloat(weightInGrams) > 0 && (
                        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-4 text-white">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-emerald-100 text-sm">Total calculado:</p>
                              <p className="text-sm text-emerald-100">
                                {parseFloat(weightInGrams) >= 1000 
                                  ? `${(parseFloat(weightInGrams) / 1000).toFixed(2).replace('.', ',')}kg` 
                                  : `${weightInGrams}g`
                                } × R$ {selectedWeightItem.price_per_kg.toFixed(2)}/kg
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-3xl font-bold">
                                R$ {calculateWeightTotal.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer do Modal */}
            <div className="border-t border-slate-200 p-4 bg-white">
              <button
                onClick={addWeightItemToCart}
                disabled={!selectedWeightItem || !weightInGrams || calculateWeightTotal <= 0}
                className={`w-full py-4 rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-3 ${
                  selectedWeightItem && weightInGrams && calculateWeightTotal > 0
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/30'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                <ShoppingCart className="w-5 h-5" />
                <span>
                  {calculateWeightTotal > 0 
                    ? `Adicionar ao carrinho • R$ ${calculateWeightTotal.toFixed(2)}`
                    : 'Selecione um item e informe o peso'
                  }
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== MODAL DE ADICIONAIS ========== */}
      {showAdditionalsModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000001] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header do Modal */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-xl">{selectedProduct.name}</h2>
                <p className="text-orange-100 text-sm">R$ {selectedProduct.price.toFixed(2)}</p>
              </div>
              <button
                onClick={() => {
                  setShowAdditionalsModal(false);
                  setSelectedProduct(null);
                  setSelectedAdditionals({});
                  setProductQuantity(1);
                  setProductGroupsWithItems([]);
                }}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
                data-testid="close-additionals-modal"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Corpo do Modal - Grupos de Adicionais */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {loadingAdditionals ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-10 h-10 text-orange-500 animate-spin mb-3" />
                  <p className="text-slate-500 font-medium">Carregando adicionais...</p>
                </div>
              ) : productGroupsWithItems.length > 0 ? (
                productGroupsWithItems.map(group => {
                  const selectedCount = (selectedAdditionals[group.id] || []).length;
                  const minItems = group.min_items || 0;
                  const maxItems = group.max_items || 10;
                  const isRequired = minItems > 0 || group.is_required;

                  return (
                    <div key={group.id} className="bg-slate-50 rounded-2xl p-4" data-testid={`additional-group-${group.id}`}>
                      {/* Header do Grupo */}
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            {group.name}
                            {isRequired && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">
                                Obrigatório
                              </span>
                            )}
                          </h3>
                          <p className="text-slate-500 text-xs">
                            {minItems > 0 ? `Mínimo ${minItems}` : 'Opcional'}
                            {maxItems < 10 && ` • Máximo ${maxItems}`}
                            {selectedCount > 0 && ` • ${selectedCount} selecionado(s)`}
                          </p>
                        </div>
                      </div>

                      {/* Adicionais do Grupo */}
                      <div className="flex flex-wrap gap-2">
                        {(group.additionals || []).filter(a => a.available !== false).map(additional => {
                          const isSelected = (selectedAdditionals[group.id] || []).some(a => a.id === additional.id);
                          
                          return (
                            <button
                              key={additional.id}
                              onClick={() => toggleAdditional(group.id, additional)}
                              data-testid={`additional-item-${additional.id}`}
                              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                                isSelected
                                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30 scale-105 ring-2 ring-orange-300'
                                  : 'bg-white text-slate-700 hover:bg-orange-50 border-2 border-slate-200 hover:border-orange-300'
                              }`}
                            >
                              {isSelected && (
                                <CheckCircle className="w-4 h-4" />
                              )}
                              <span>{additional.name}</span>
                              {additional.price > 0 && (
                                <span className={`text-xs ${isSelected ? 'text-orange-100' : 'text-slate-400'}`}>
                                  +R$ {additional.price.toFixed(2)}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Nenhum adicional disponível</p>
                  <p className="text-sm text-slate-400 mt-1">Este produto não possui adicionais configurados</p>
                </div>
              )}
            </div>

            {/* Footer do Modal */}
            <div className="border-t border-slate-200 p-4 bg-white">
              {/* Quantidade */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <span className="text-slate-600 font-medium">Quantidade:</span>
                <div className="flex items-center gap-2 bg-slate-100 rounded-xl p-1">
                  <button
                    onClick={() => setProductQuantity(Math.max(1, productQuantity - 1))}
                    className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-all shadow-sm"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="w-12 text-center font-bold text-lg text-slate-800">{productQuantity}</span>
                  <button
                    onClick={() => setProductQuantity(productQuantity + 1)}
                    className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-all shadow-sm"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Botão Adicionar */}
              <button
                onClick={addToCartWithAdditionals}
                disabled={!canAddToCart}
                className={`w-full py-4 rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-3 ${
                  canAddToCart
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-500/30'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                <ShoppingCart className="w-5 h-5" />
                <span>Adicionar • R$ {calculateProductTotal.toFixed(2)}</span>
              </button>

              {!canAddToCart && (
                <p className="text-center text-red-500 text-sm mt-2">
                  Selecione os itens obrigatórios
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default PDVScreen;
